import { useMemo, useState } from "react";
import { exportAssignmentsCsv } from "./rosterUtils";
import { rosterTheme as t } from "./rosterTheme";

const PAGE_SIZE = 25;

export default function AssignmentGrid({
  rows,
  loading,
  canEdit,
  isAdmin,
  onEdit,
  onDelete,
  onDuplicate,
  onCopyWeek,
}) {
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("work_date");
  const [sortDir, setSortDir] = useState("asc");
  const [page, setPage] = useState(1);
  const [deptFilter, setDeptFilter] = useState("");

  const safeRows = rows || [];

  const departments = useMemo(() => {
    const s = new Set(safeRows.map((r) => r.department).filter(Boolean));
    return [...s].sort();
  }, [safeRows]);

  const filtered = useMemo(() => {
    let list = [...safeRows];
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(
        (r) =>
          r.employee_name?.toLowerCase().includes(q) ||
          r.employee_code?.toLowerCase().includes(q) ||
          r.shift_name?.toLowerCase().includes(q)
      );
    }
    if (deptFilter) list = list.filter((r) => r.department === deptFilter);
    list.sort((a, b) => {
      const av = a[sortKey] ?? "";
      const bv = b[sortKey] ?? "";
      const cmp = String(av).localeCompare(String(bv));
      return sortDir === "asc" ? cmp : -cmp;
    });
    return list;
  }, [safeRows, search, deptFilter, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const SortTh = ({ col, label }) => (
    <th
      className="p-2 text-left text-xs font-semibold text-slate-600 cursor-pointer whitespace-nowrap sticky top-0 bg-slate-50 z-10"
      onClick={() => toggleSort(col)}
    >
      {label} {sortKey === col ? (sortDir === "asc" ? "↑" : "↓") : ""}
    </th>
  );

  return (
    <div className={`${t.card} overflow-hidden`}>
      <div className="flex flex-wrap gap-2 p-3 border-b border-slate-100 bg-slate-50/80">
        <input
          className={`${t.input} !py-1.5 flex-1 min-w-[160px]`}
          placeholder="Search employee, code, shift…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />
        <select
          className={`${t.select} !py-1.5`}
          value={deptFilter}
          onChange={(e) => {
            setDeptFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="">All departments</option>
          {departments.map((d) => (
            <option key={d} value={d}>{d}</option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => exportAssignmentsCsv(filtered)}
          className={t.btnSecondary + " !py-1.5 !px-3"}
        >
          Export CSV
        </button>
      </div>

      <div className="overflow-auto max-h-[480px]">
        <table className="w-full text-sm min-w-[900px]">
          <thead>
            <tr className="border-b">
              <SortTh col="employee_name" label="Employee" />
              <SortTh col="employee_code" label="Code" />
              <SortTh col="department" label="Department" />
              <SortTh col="work_date" label="Date" />
              <SortTh col="shift_name" label="Shift" />
              <th className="p-2 text-left text-xs font-semibold text-gray-600 sticky top-0 bg-gray-50">Timing</th>
              <SortTh col="status" label="Status" />
              <th className="p-2 text-left text-xs font-semibold text-gray-600 sticky top-0 bg-gray-50">Notes</th>
              <th className="p-2 text-left text-xs font-semibold text-gray-600 sticky top-0 bg-gray-50">Updated</th>
              <th className="p-2 sticky top-0 bg-gray-50 z-10" />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              [...Array(5)].map((_, i) => (
                <tr key={i} className="border-t animate-pulse">
                  <td colSpan={10} className="p-3 h-10 bg-gray-100" />
                </tr>
              ))
            ) : pageRows.length === 0 ? (
              <tr>
                <td colSpan={10} className="p-8 text-center text-gray-500">
                  No assignments yet. Use the planner or add assignment form.
                </td>
              </tr>
            ) : (
              pageRows.map((r) => (
                <tr key={r.assignment_id} className="border-t hover:bg-gray-50/50">
                  <td className="p-2 font-medium">{r.employee_name}</td>
                  <td className="p-2 text-gray-600">{r.employee_code}</td>
                  <td className="p-2 text-gray-600">{r.department || "—"}</td>
                  <td className="p-2">{r.work_date}</td>
                  <td className="p-2">{r.shift_name || "—"}</td>
                  <td className="p-2 text-gray-600 text-xs">{r.shift_timing || "—"}</td>
                  <td className="p-2">
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${
                        r.status === "active" ? "bg-green-100 text-green-800" : "bg-gray-100"
                      }`}
                    >
                      {r.status}
                    </span>
                  </td>
                  <td className="p-2 text-xs text-gray-500 max-w-[120px] truncate">{r.notes || "—"}</td>
                  <td className="p-2 text-xs text-gray-500">
                    {r.updated_at ? new Date(r.updated_at).toLocaleString() : "—"}
                  </td>
                  <td className="p-2 whitespace-nowrap">
                    {canEdit && (
                      <>
                        <button
                          type="button"
                          className={t.btnGhost + " !text-xs mr-2"}
                          onClick={() => onEdit(r)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="text-red-600 text-xs mr-2"
                          onClick={() => onDelete(r)}
                        >
                          Delete
                        </button>
                        <button
                          type="button"
                          className="text-gray-600 text-xs mr-2"
                          onClick={() => onDuplicate(r)}
                        >
                          Duplicate
                        </button>
                      </>
                    )}
                    {isAdmin && onCopyWeek && (
                      <button
                        type="button"
                        className="text-gray-600 text-xs"
                        onClick={() => onCopyWeek(r)}
                      >
                        Copy week
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between px-3 py-2 border-t text-sm text-gray-600">
        <span>
          {filtered.length} assignment{filtered.length !== 1 ? "s" : ""}
        </span>
        <div className="flex gap-2 items-center">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
            className="px-2 py-1 border rounded disabled:opacity-40"
          >
            Prev
          </button>
          <span>
            {page} / {totalPages}
          </span>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="px-2 py-1 border rounded disabled:opacity-40"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
